import { useId, useRef, useState, type ComponentProps, type ReactNode, type Ref } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { cn, formatNumber, normalizeLocale } from '../lib/utils';

/** Wins over the caller's classes: a filled button variant reads as a selected amount, fighting the error. */
const errorBoxClassName =
	'border-destructive focus-within:border-destructive text-destructive focus-within:text-destructive bg-transparent hover:bg-transparent';

/** Through the locale formatter: `String(12.5)` reads back as `125` where the separator is a comma. */
export function amountToDisplay(locale: string, amount: number | undefined): string {
	if (amount == null || amount <= 0) return '';
	return formatNumber(locale, amount, Number.isInteger(amount) ? 0 : 2);
}

type AmountInputProps = Omit<
	ComponentProps<typeof CurrencyInput>,
	| 'value'
	| 'onValueChange'
	| 'intlConfig'
	| 'decimalSeparator'
	| 'groupSeparator'
	| 'suffix'
	| 'prefix'
	| 'className'
	| 'style'
	| 'ref'
	// Owned by `errorMessage`, so a caller cannot half-wire the pair.
	| 'aria-invalid'
	| 'aria-describedby'
	// The width comes from the mirror below, so a character count would do nothing.
	| 'size'
> & {
	locale: string;
	/** Exactly as typed. */
	value: string;
	/** `amount` is 0 when empty. */
	onValueChange: (display: string, amount: number) => void;
	/** Only once something is typed. */
	suffix?: ReactNode;
	/** For a field that already owns an amount; a field offering a fresh one wants nothing to undo first. */
	autoFillOnFocus?: number;
	/** Also sets `aria-invalid`, so a form scrolling to the first invalid field lands on the input. */
	errorMessage?: ReactNode;
	/** The visible box and the click target. */
	className?: string;
	/** Lands on the sizer, so the hidden mirror the width is measured from shares the typography. */
	inputClassName?: string;
	/** So `react-hook-form` can focus the field it refuses to submit. */
	ref?: Ref<HTMLInputElement>;
};

/**
 * The caller owns the display string: a numeric `value` breaks decimal commas mid-typing. Never rejects a
 * keystroke; limits are the caller's validation, which can show an error.
 */
export function AmountInput({
	locale,
	value,
	onValueChange,
	suffix = '€',
	autoFillOnFocus,
	errorMessage,
	decimalsLimit = 2,
	allowNegativeValue = false,
	className,
	inputClassName,
	placeholder,
	onFocus,
	onBlur,
	ref,
	...props
}: AmountInputProps) {
	const inputRef = useRef<HTMLInputElement>(null);
	const errorId = useId();
	const [isFocused, setIsFocused] = useState(false);
	const hasValue = value !== '';
	const hasError = !!errorMessage;

	// Focus drops the placeholder, so the caret sits against the currency.
	const shownText = hasValue ? value : isFocused ? '' : (placeholder ?? '');

	return (
		<div className="flex flex-col gap-1">
			<div
				// Trailing classes are invariants: the number-to-suffix gap is the sizer's slack.
				className={cn(
					'flex items-center justify-center',
					className,
					'cursor-text gap-0 whitespace-nowrap hover:cursor-text',
					hasError && errorBoxClassName,
				)}
				onClick={(event) => {
					if (!(event.target as HTMLElement).closest('input')) inputRef.current?.focus();
				}}
			>
				{/* Not `field-sizing: content`: WebKit's ~2px caret overflow shaves the first digit on iPhone. */}
				<span className={cn('relative inline-block min-w-0 shrink overflow-hidden', inputClassName, 'px-[3px]')}>
					<span aria-hidden="true" className="invisible block whitespace-pre">
						{shownText === '' ? '\u00A0' : shownText}
					</span>
					<CurrencyInput
						ref={(node: HTMLInputElement | null) => {
							inputRef.current = node;
							if (typeof ref === 'function') ref(node);
							else if (ref) ref.current = node;
						}}
						className="absolute inset-0 w-full bg-transparent text-center text-inherit outline-none placeholder:text-inherit"
						placeholder={isFocused ? undefined : placeholder}
						value={value}
						intlConfig={{ locale: normalizeLocale(locale) }}
						decimalsLimit={decimalsLimit}
						allowNegativeValue={allowNegativeValue}
						step={1}
						onValueChange={(display, _name, values) => onValueChange(display ?? '', values?.float ?? 0)}
						onFocus={(event) => {
							onFocus?.(event);
							setIsFocused(true);
							if (!hasValue && autoFillOnFocus != null && autoFillOnFocus > 0) {
								onValueChange(amountToDisplay(locale, autoFillOnFocus), autoFillOnFocus);
							}
							// Not a selection: on mobile it raises the handles and the paste bubble.
							const input = event.currentTarget;
							requestAnimationFrame(() => input.setSelectionRange(input.value.length, input.value.length));
						}}
						onBlur={(event) => {
							onBlur?.(event);
							setIsFocused(false);
						}}
						{...props}
						aria-invalid={hasError}
						aria-describedby={hasError ? errorId : undefined}
					/>
				</span>
				{(hasValue || isFocused) && suffix != null && (
					<span className="pointer-events-none shrink-0 select-none">{suffix}</span>
				)}
			</div>
			{/* No `role="alert"`: validated per keystroke, it would talk over the donor typing. */}
			{hasError && (
				<p id={errorId} data-slot="form-message" className="text-destructive m-1 text-sm">
					{errorMessage}
				</p>
			)}
		</div>
	);
}
